"use client";

import { useEffect, useState } from 'react';

/**
 * Platform detection helpers.
 *
 * The mobile app (App Store) is a react-native-webview wrapper around the live
 * website. The WebView injects `window.ReactNativeWebView` into every page.
 * A normal browser (Safari/Chrome on the website) does NOT have this object.
 *
 * We use this to render certain admin-only / heavy features on the WEBSITE only
 * and hide them inside the app — one codebase, zero risk to the working app
 * (the app is not rebuilt; it just won't render the gated blocks).
 */

/** Synchronous check. Returns false during SSR (no window). */
export function isNativeApp(): boolean {
  if (typeof window === 'undefined') return false;
  // react-native-webview injects this object into the page context.
  return !!(window as unknown as { ReactNativeWebView?: unknown }).ReactNativeWebView;
}

/**
 * Hook version safe for static export + hydration.
 *
 * Returns:
 *  - `null`  while unknown (initial render / before mount) — render nothing platform-specific yet
 *  - `true`  if running inside the native app WebView
 *  - `false` if running on the plain website
 *
 * Gate web-only features with `isNative === false` so they:
 *  - never cause a hydration mismatch (first render is identical on server & client),
 *  - never flash inside the app (they only appear once we've confirmed it's the web).
 */
export function useIsNativeApp(): boolean | null {
  const [isNative, setIsNative] = useState<boolean | null>(null);

  useEffect(() => {
    // Intentional: the value can only be known on the client, and the first
    // render must match the server (null) to avoid a hydration mismatch.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setIsNative(isNativeApp());
  }, []);

  return isNative;
}
